import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { loginSchema, type LoginFormData } from '@/schemas/auth.schema';
import { useAuthContext } from '@/hooks/useAuthContext';
import logo from '@/assets/logo.png';

export default function LoginPage() {
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const ROLE_HOME: Record<string, string> = {
    SUPER_ADMIN: '/dashboard',
    ADMIN: '/dashboard',
    MANAGER: '/dashboard',
    ACCOUNTANT: '/accounting/export',
    LEAD: '/my-tasks',
    TECH: '/my-tasks',
    CUSTOMER: '/portal',
  };

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      const result = await login(data.username, data.password);
      const returnUrl = searchParams.get('returnUrl');
      const dest =
        returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')
          ? returnUrl
          : (ROLE_HOME[result.user.role] ?? '/dashboard');
      navigate(dest, { replace: true });
    } catch {
      setError('Invalid username or password');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand Panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 flex-col justify-between p-10 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        {/* Decorative accent */}
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute -top-10 -left-10 w-60 h-60 rounded-full bg-blue-500/5 blur-2xl" />

        <div className="relative z-10">
          <img
            src={logo}
            alt="BRO Autopaint Garage"
            className="h-14 w-14 rounded-xl object-cover shadow-lg"
          />
        </div>

        <div className="relative z-10 space-y-4">
          <h2 className="text-3xl font-bold text-white leading-tight">
            Workshop
            <br />
            Management System
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
            Manage inquiries, jobs, invoices, and workshop operations from a single dashboard.
          </p>
        </div>

        <p className="relative z-10 text-slate-500 text-xs">
          BRO Autopaint Garage &middot; Shah Alam, Selangor
        </p>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img
              src={logo}
              alt="BRO Autopaint Garage"
              className="h-10 w-10 rounded-lg object-cover shadow"
            />
            <div>
              <h1 className="text-lg font-bold text-foreground leading-tight">BengkelOS</h1>
              <p className="text-xs text-muted-foreground">Workshop Management System</p>
            </div>
          </div>

          {/* Desktop heading */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your staff account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Username</label>
              <input
                {...register('username')}
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
                placeholder="Enter your username"
                autoComplete="username"
              />
              {errors.username && (
                <p className="text-destructive text-xs mt-1.5">{errors.username.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                {...register('password')}
                className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="text-destructive text-xs mt-1.5">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg px-3.5 py-2.5">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-50 transition-colors cursor-pointer min-h-[44px]"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Customer portal link */}
          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-center text-sm text-muted-foreground">
              Customer?{' '}
              <Link
                to="/portal/join"
                className="text-primary font-medium hover:underline cursor-pointer"
              >
                Track your car here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
